/**
 * The global filter bar's state, and its one home: the URL.
 *
 * Every page reads the same filters from the query string, so a link copied
 * from any screen reopens it exactly, and a drill-down from one page to the
 * next carries the context with it (pivot doc §3, drill-down contract). This
 * module is pure — parsing, serialising and resolving date windows — so it can
 * be tested without a router; `useGlobalFilters` binds it to the location.
 *
 * Defaults are omitted from the URL rather than written out, the same rule the
 * environment filter follows on the wire: an unadorned URL is the default view.
 */

import type { Environment } from '@/lib/api'

export const DATE_PRESETS = [
  'today',
  'yesterday',
  '7d',
  '30d',
  'mtd',
  'previous_month',
  'custom',
] as const
export type DatePreset = (typeof DATE_PRESETS)[number]

export const DATE_PRESET_LABELS: Record<DatePreset, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  mtd: 'Month to date',
  previous_month: 'Previous month',
  custom: 'Custom range',
}

export const COMPARISONS = ['previous_period', 'previous_year', 'none'] as const
export type Comparison = (typeof COMPARISONS)[number]

export const COMPARISON_LABELS: Record<Comparison, string> = {
  previous_period: 'Previous period',
  previous_year: 'Same period last year',
  none: 'No comparison',
}

export const DIMENSIONS = [
  'website',
  'competition',
  'channel',
  'campaign',
  'creative',
  'device',
  'customer_type',
] as const
export type Dimension = (typeof DIMENSIONS)[number]

export const DIMENSION_LABELS: Record<Dimension, string> = {
  website: 'Website',
  competition: 'Competition',
  channel: 'Channel',
  campaign: 'Campaign',
  creative: 'Creative',
  device: 'Device',
  customer_type: 'Customer type',
}

const ENVIRONMENTS: readonly Environment[] = ['production', 'staging', 'test', 'development']

export interface GlobalFilters {
  range: DatePreset
  /** ISO dates, set only for a custom range. */
  from: string | null
  to: string | null
  compare: Comparison
  /** Empty means the production default, decided by the server. */
  environments: Environment[]
  dimensions: Partial<Record<Dimension, string>>
}

export const DEFAULT_FILTERS: GlobalFilters = {
  range: '30d',
  from: null,
  to: null,
  compare: 'previous_period',
  environments: [],
  dimensions: {},
}

/** Every query key the filter bar owns. Anything else belongs to the page. */
export const FILTER_PARAM_KEYS: readonly string[] = [
  'range',
  'from',
  'to',
  'compare',
  'environment',
  ...DIMENSIONS,
]

const MAX_DIMENSION_VALUE = 128

function oneOf<T extends string>(values: readonly T[], candidate: string | null): candidate is T {
  return candidate !== null && (values as readonly string[]).includes(candidate)
}

export function isIsoDate(value: string | null): value is string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const parsed = new Date(`${value}T00:00:00Z`)
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().startsWith(value)
}

export function parseFilters(params: URLSearchParams): GlobalFilters {
  const rangeParam = params.get('range')
  let range: DatePreset = oneOf(DATE_PRESETS, rangeParam) ? rangeParam : DEFAULT_FILTERS.range
  let from: string | null = null
  let to: string | null = null

  if (range === 'custom') {
    const fromParam = params.get('from')
    const toParam = params.get('to')
    if (isIsoDate(fromParam) && isIsoDate(toParam)) {
      ;[from, to] = fromParam <= toParam ? [fromParam, toParam] : [toParam, fromParam]
    } else {
      // A custom range with no usable dates is not a range; fall back rather
      // than render a page over an undefined window.
      range = DEFAULT_FILTERS.range
    }
  }

  const compareParam = params.get('compare')
  const compare = oneOf(COMPARISONS, compareParam) ? compareParam : DEFAULT_FILTERS.compare

  const environments = [
    ...new Set(params.getAll('environment').filter((value) => oneOf(ENVIRONMENTS, value))),
  ] as Environment[]

  const dimensions: Partial<Record<Dimension, string>> = {}
  for (const dimension of DIMENSIONS) {
    const value = params.get(dimension)?.trim()
    if (value && value.length <= MAX_DIMENSION_VALUE) dimensions[dimension] = value
  }

  return { range, from, to, compare, environments, dimensions }
}

/**
 * Writes `filters` into a copy of `base`, keeping any page-owned parameters
 * (a funnel `stage`, a report `metric`) that are already there.
 */
export function serializeFilters(
  filters: GlobalFilters,
  base: URLSearchParams = new URLSearchParams(),
): URLSearchParams {
  const next = new URLSearchParams(base)
  for (const key of FILTER_PARAM_KEYS) next.delete(key)

  if (filters.range !== DEFAULT_FILTERS.range) next.set('range', filters.range)
  if (filters.range === 'custom' && filters.from && filters.to) {
    next.set('from', filters.from)
    next.set('to', filters.to)
  }
  if (filters.compare !== DEFAULT_FILTERS.compare) next.set('compare', filters.compare)
  for (const environment of filters.environments) next.append('environment', environment)
  for (const dimension of DIMENSIONS) {
    const value = filters.dimensions[dimension]
    if (value) next.set(dimension, value)
  }
  return next
}

export function activeDimensionCount(filters: GlobalFilters): number {
  return DIMENSIONS.filter((dimension) => filters.dimensions[dimension]).length
}

// ---------------------------------------------------------------------------
// Date windows. Dates are calendar days as ISO strings ("2026-09-12"), and all
// arithmetic happens in UTC so a daylight-saving change cannot shift a day.
// ---------------------------------------------------------------------------

export interface DateWindow {
  from: string
  to: string
  days: number
}

export interface ResolvedPeriod {
  current: DateWindow
  previous: DateWindow | null
  label: string
  /** "vs previous 30 days", or null when comparison is off. */
  compareLabel: string | null
}

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

/** The viewer's local calendar date. */
export function toDateKey(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function parts(key: string): [number, number, number] {
  const [year, month, day] = key.split('-').map(Number) as [number, number, number]
  return [year, month, day]
}

function fromUtc(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10)
}

export function addDays(key: string, days: number): string {
  const [year, month, day] = parts(key)
  return fromUtc(Date.UTC(year, month - 1, day + days))
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

function addMonthsClamped(key: string, months: number): string {
  const [year, month, day] = parts(key)
  const first = new Date(Date.UTC(year, month - 1 + months, 1))
  const targetYear = first.getUTCFullYear()
  const targetMonth = first.getUTCMonth() + 1
  return `${targetYear}-${pad(targetMonth)}-${pad(Math.min(day, daysInMonth(targetYear, targetMonth)))}`
}

export function dayCount(from: string, to: string): number {
  const [fy, fm, fd] = parts(from)
  const [ty, tm, td] = parts(to)
  return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86_400_000) + 1
}

/** Every day in the window, oldest first. */
export function eachDay(window: DateWindow): string[] {
  return Array.from({ length: window.days }, (_, index) => addDays(window.from, index))
}

function makeWindow(from: string, to: string): DateWindow {
  return { from, to, days: dayCount(from, to) }
}

export function currentWindow(filters: GlobalFilters, now: Date): DateWindow {
  const today = toDateKey(now)
  switch (filters.range) {
    case 'today':
      return makeWindow(today, today)
    case 'yesterday': {
      const yesterday = addDays(today, -1)
      return makeWindow(yesterday, yesterday)
    }
    case '7d':
      return makeWindow(addDays(today, -6), today)
    case '30d':
      return makeWindow(addDays(today, -29), today)
    case 'mtd':
      return makeWindow(`${today.slice(0, 8)}01`, today)
    case 'previous_month': {
      const firstOfThisMonth = `${today.slice(0, 8)}01`
      return makeWindow(`${addDays(firstOfThisMonth, -1).slice(0, 8)}01`, addDays(firstOfThisMonth, -1))
    }
    case 'custom':
      return filters.from && filters.to
        ? makeWindow(filters.from, filters.to)
        : makeWindow(addDays(today, -29), today)
  }
}

function previousWindow(filters: GlobalFilters, current: DateWindow): DateWindow | null {
  switch (filters.compare) {
    case 'none':
      return null
    case 'previous_year':
      return makeWindow(addMonthsClamped(current.from, -12), addMonthsClamped(current.to, -12))
    case 'previous_period':
      if (filters.range === 'mtd' || filters.range === 'previous_month') {
        // Calendar-aligned: September 1-12 compares with August 1-12, and a
        // whole month with the whole month before it.
        const from = addMonthsClamped(current.from, -1)
        const to =
          filters.range === 'previous_month'
            ? addDays(current.from, -1)
            : addMonthsClamped(current.to, -1)
        return makeWindow(from, to)
      }
      return makeWindow(addDays(current.from, -current.days), addDays(current.from, -1))
  }
}

const DAY_MONTH = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })
const DAY_MONTH_YEAR = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
})

function utcDate(key: string): Date {
  const [year, month, day] = parts(key)
  return new Date(Date.UTC(year, month - 1, day))
}

export function formatDateKey(key: string, withYear = false): string {
  return (withYear ? DAY_MONTH_YEAR : DAY_MONTH).format(utcDate(key))
}

export function formatWindow(window: DateWindow): string {
  if (window.days === 1) return formatDateKey(window.from, true)
  return `${formatDateKey(window.from)} – ${formatDateKey(window.to, true)}`
}

function compareLabelFor(filters: GlobalFilters, current: DateWindow): string | null {
  switch (filters.compare) {
    case 'none':
      return null
    case 'previous_year':
      return 'vs same period last year'
    case 'previous_period':
      switch (filters.range) {
        case 'today':
          return 'vs yesterday'
        case 'yesterday':
          return 'vs the day before'
        case 'mtd':
          return 'vs same days last month'
        case 'previous_month':
          return 'vs the month before'
        default:
          return `vs previous ${current.days} days`
      }
  }
}

export function resolvePeriod(filters: GlobalFilters, now: Date): ResolvedPeriod {
  const current = currentWindow(filters, now)
  return {
    current,
    previous: previousWindow(filters, current),
    label: filters.range === 'custom' ? formatWindow(current) : DATE_PRESET_LABELS[filters.range],
    compareLabel: compareLabelFor(filters, current),
  }
}
